import {makeAutoObservable, runInAction} from "mobx";
import {runFetch} from "../util/runFetch";
import CsInstance from "./CsInstance";

const FEATURE_FLAG_KEYS = [
	"showExtraButtons",
	"showActivityDropdown",
	"showHiddenFiles",
	"useMonaco",
	"useReadOnlyRanges",
	"hideCompileButton",
	"loadPrevProject",
	"theaterSizeFixed",
	"useBjvm",
	"usePython",
] as const;

/**
 * Options to force feature flags to be enabled or disabled.
 */
export type FeatureFlagStoreOpts = {
	[key in (typeof FEATURE_FLAG_KEYS)[number]]?: boolean;
};

/**
 * This store is used to manage feature flags, toggles to control selectively
 * turning on features that are either experimental or only relevant for certain
 * consumers.
 *
 * Feature flags should:
 *   - not be mutable at runtime (except in dev mode maybe).
 *   - default to false.
 */
export class FeatureFlagStore {
	private _showExtraButtons = false;
	private _showActivityDropdown = false;
	private _showHiddenFiles = false; // allow curriculum to edit while viewing hidden files
	private _useMonaco = true;
	private _useReadOnlyRanges = false; // turn off snippets unless we want them
	private _hideCompileButton = false;
	private _loadPrevProject = false;
	private _theaterSizeFixed = false;
	private _useBjvm = false;
	private _usePython = false;

	// non-flags
	private _csInstance: CsInstance;
	private _opts?: FeatureFlagStoreOpts;
	private _flagsReady = false;
	private _onFlagsReady: Function[] = [];

	constructor(props: {opts?: FeatureFlagStoreOpts; csInstance: CsInstance}) {
		this._csInstance = props.csInstance;
		this._opts = props.opts;

		makeAutoObservable(this);
	}

	/**
	 * This function is to be called when the main CsController component has rendered to evaluate feature flags.
	 * Feature flags are evaluated in the following order of precedence:
	 * 1. If the feature flag is set by opts, use that value.
	 * 2. Feature flags may be set by query params if not already set via opts.
	 * 3. Feature flags may be set by LaunchDarkly if not already set via opts or query params.
	 * 4. If the flag is not defined by any of the above, it will default to false.
	 */
	async evaluateFlags() {
		// Fetch feature flags from the server
		const launchDarklyFlags = await runFetch({
			url: `${this._csInstance.serverUrl}/api/v2/evaluateFlags`,
			method: "GET",
			securityToken: this._csInstance.securityToken,
		});

		// https://stackoverflow.com/questions/64770762 to handle async actions in MobX
		runInAction(() => {
			const query = new URLSearchParams(window.location.search);
			for (const key of FEATURE_FLAG_KEYS) {
				// Check if the feature flag is set by opts
				if (this._opts?.hasOwnProperty(key)) {
					this[`_${key}`] = !!this._opts[key];
				} else {
					// Check if the feature flag is set by query params
					if (query.has(key)) {
						this[`_${key}`] = query.get(key) === "true";
					} else {
						// Check if the feature flag is set by LaunchDarkly
						if (launchDarklyFlags.hasOwnProperty(key)) {
							this[`_${key}`] = Boolean(launchDarklyFlags[key]);
						}
					}
				}
			}
			this._flagsReady = true;
			this._onFlagsReady.forEach((callback) => callback());
		});
	}

	// Promise resolves when the flags have been updated to their final values.
	flagsReady(): Promise<void> {
		if (this._flagsReady) {
			return Promise.resolve();
		}
		return new Promise((resolve, _reject) => {
			this._onFlagsReady.push(resolve);
		});
	}

	// autogen getters for MobX actions
	// feature flags are NOT meant to be mutable at runtime, so no setters

	get showExtraButtons() {
		return this._showExtraButtons;
	}

	get showActivityDropdown() {
		return this._showActivityDropdown;
	}

	get showHiddenFiles() {
		return this._showHiddenFiles;
	}

	get useMonaco() {
		return this._useMonaco;
	}

	get useReadOnlyRanges() {
		return this._useReadOnlyRanges;
	}

	get hideCompileButton() {
		return this._hideCompileButton;
	}

	get loadPrevProject() {
		return this._loadPrevProject;
	}

	get theaterSizeFixed() {
		return this._theaterSizeFixed;
	}

	get bjvmEnabled() {
		return this._useBjvm;
	}

	get usePython() {
		return this._usePython;
	}
}

export default FeatureFlagStore;
